import type { Kanji } from '@obv/api-client';
import { convert, formatAmount, formatRate, slideshowOrder, slideshowStart } from '@obv/core';
import { useEffect, useState } from 'react';
import { useLocation } from 'wouter';
import { canSpeak, speakJapanese } from '@/lib/speech';
import { useExchangeRates, useKanji, useVerseOfDay } from '@/queries/content';
import { useUpdatePreferences } from '@/queries/hooks';
import { useDisplay } from '../context';
import { useDisplayUi } from '../store';
import { Card, Unavailable } from '../ui';

const SLIDE_MS = 12_000;

function Head({ title, link, onLink }: { title: string; link: string; onLink: () => void }) {
  return (
    <div className="flex flex-none items-baseline justify-between">
      <h2 className="text-[17px] font-normal">{title}</h2>
      <button type="button" className="tap -my-3 h-[44px] text-[13px] font-medium" onClick={onLink}>
        {link}
      </button>
    </div>
  );
}

/** Today's verse: Arabic, translation and reference; tapping opens the Quran at that ayah. */
export function VerseWidget({ narrow = false }: { narrow?: boolean }) {
  const ctx = useDisplay();
  const [, navigate] = useLocation();
  const openQuranAt = useDisplayUi((s) => s.openQuranAt);
  const translation = ctx.prefs.quran_translation as 'en' | 'bn';
  const q = useVerseOfDay(ctx.clock.dateKey, translation);
  const v = q.data;
  const open = () => {
    if (v) openQuranAt({ surah: v.surah, ayah: v.ayah });
    navigate('/display/quran');
  };
  return (
    <Card tone={9} className="flex flex-col overflow-hidden px-6 pb-4 pt-[18px]">
      <Head title="Verse of the day" link="Quran ↗" onLink={open} />
      {!v ? (
        <Unavailable>{q.isLoading ? 'Finding today’s verse…' : 'The verse is unavailable right now.'}</Unavailable>
      ) : (
        <button type="button" onClick={open} className="tap scroll-y flex min-h-0 flex-1 flex-col text-left" aria-label={`${v.reference}: ${v.translation}`}>
          {/* my-auto centres short verses; long ones scroll inside the card. */}
          <span className="my-auto block">
            <p className="quran-text text-right leading-[1.9]" dir="rtl" style={{ fontSize: narrow ? 22 : 28 }}>
              {v.arabic}
            </p>
            <p className="mt-2 text-muted2" style={{ fontSize: narrow ? 14 : 16, lineHeight: 1.5 }} lang={translation}>
              {v.translation}
            </p>
            <p className="mt-2 text-[12px] font-semibold uppercase tracking-eyebrow text-muted">{v.reference}</p>
          </span>
        </button>
      )}
    </Card>
  );
}

/** The saved pair (JPY → BDT by default), with a swap button; tapping opens the converter. */
export function ExchangeWidget({ narrow = false }: { narrow?: boolean }) {
  const ctx = useDisplay();
  const [, navigate] = useLocation();
  const prefs = useUpdatePreferences();
  const rates = useExchangeRates();
  const { fx_from: from, fx_to: to, fx_amount: amount } = ctx.prefs;
  const list = rates.data ? rates.data.currencies : [];
  const f = list.find((c) => c.code === from);
  const t = list.find((c) => c.code === to);
  return (
    <Card tone={3} className="flex flex-col overflow-hidden px-6 pb-4 pt-[18px]">
      <Head title="Exchange rate" link="Convert ↗" onLink={() => navigate('/display/exchange')} />
      {!f || !t ? (
        <Unavailable>{rates.isLoading || (rates.error && rates.error.code === 'exchange.pending') ? 'Fetching rates…' : 'Rates unavailable right now.'}</Unavailable>
      ) : (
        <div className="flex min-h-0 flex-1 flex-col justify-center">
          <div className="tabular text-muted2" style={{ fontSize: narrow ? 16 : 20 }}>
            {f.flag} {formatAmount(amount, f.minor_units)} {from}
          </div>
          <div className="tabular mt-1 whitespace-nowrap font-semibold leading-none tracking-[-0.02em]" style={{ fontSize: narrow ? 30 : 44 }}>
            {narrow ? '' : `${t.flag} `}
            {formatAmount(convert(amount, f.per_usd, t.per_usd), t.minor_units)}
            <span className="ml-2 font-normal text-muted2" style={{ fontSize: narrow ? 16 : 22 }}>
              {to}
            </span>
          </div>
          <div className="mt-3 flex items-center justify-between">
            <span className="tabular text-[13px] text-muted">
              1 {from} = {formatRate(convert(1, f.per_usd, t.per_usd))} {to}
            </span>
            <button
              type="button"
              onClick={() => prefs.mutate({ fx_from: to, fx_to: from })}
              aria-label={`Swap to ${to} → ${from}`}
              className="tap flex h-[44px] w-[44px] flex-none items-center justify-center rounded-full border text-[20px]"
              style={{ borderColor: 'var(--bd)' }}
            >
              ⇄
            </button>
          </div>
        </div>
      )}
    </Card>
  );
}

/** The most common kanji (the 80 at JLPT N5) as a slideshow, a new one every few seconds. */
export function KanjiSlideshowWidget({ narrow = false }: { narrow?: boolean }) {
  const ctx = useDisplay();
  const [, navigate] = useLocation();
  const openKanji = useDisplayUi((s) => s.openKanji);
  const editing = useDisplayUi((s) => s.homeDraft !== null);
  const q = useKanji();
  const sorted: Kanji[] = q.data ? slideshowOrder(q.data) : [];
  const common = sorted.filter((k) => k.level === 'N5');
  const order = common.length ? common : sorted;
  const [step, setStep] = useState(0);
  const [visible, setVisible] = useState(true);
  useEffect(() => {
    if (!order.length || editing) return;
    let fade: ReturnType<typeof setTimeout>;
    const id = setInterval(() => {
      // Fade out, change, fade in: an opacity transition is cheap enough for the A7.
      setVisible(false);
      fade = setTimeout(() => {
        setStep((s) => s + 1);
        setVisible(true);
      }, 350);
    }, SLIDE_MS);
    return () => {
      clearInterval(id);
      clearTimeout(fade);
    };
  }, [order.length, editing]);

  const k = order.length ? order[(slideshowStart(order.length, ctx.clock.dateKey) + step) % order.length]! : null;
  const open = () => {
    if (k) openKanji(k.id);
    navigate('/display/japanese');
  };
  const word = k && k.words[0];
  return (
    <Card tone={10} className="flex flex-col overflow-hidden px-6 pb-4 pt-[18px]">
      <Head title="Kanji" link="Study ↗" onLink={open} />
      {!k ? (
        <Unavailable>{q.isLoading ? 'Loading kanji…' : 'Kanji unavailable right now.'}</Unavailable>
      ) : (
        <div className="flex min-h-0 flex-1 items-center" style={{ opacity: visible ? 1 : 0, transition: 'opacity 350ms ease' }}>
          <button
            type="button"
            onClick={open}
            className="tap jp-text flex-none leading-none"
            style={{ fontSize: narrow ? 76 : 104 }}
            aria-label={`${k.char}: ${k.meanings.join(', ')}. Open in Japanese`}
          >
            {k.char}
          </button>
          <div className="ml-5 min-w-0 flex-1">
            <div className="font-semibold leading-tight" style={{ fontSize: narrow ? 16 : 22 }}>
              {k.meanings.slice(0, 2).join(', ')}
            </div>
            <div className="jp-text ellipsis mt-1 text-[16px] text-muted2">{[...k.on.slice(0, 2), ...k.kun.slice(0, 2)].join('、')}</div>
            {word && !narrow ? (
              <div className="mt-2 text-[14px]">
                <span className="jp-text text-[18px]">{word.word}</span> <span className="text-muted2">{word.reading}</span> · {word.meaning}
              </div>
            ) : null}
            <div className="mt-2 flex items-center">
              <span className="rounded-full border px-2 py-[2px] text-[11px] font-semibold" style={{ borderColor: 'var(--bd)' }}>
                {k.level}
              </span>
              {canSpeak() ? (
                <button
                  type="button"
                  onClick={() => speakJapanese(word ? word.word : k.char)}
                  aria-label={`Hear ${word ? word.word : k.char}`}
                  className="tap ml-2 flex h-[36px] w-[36px] items-center justify-center rounded-full border text-[14px]"
                  style={{ borderColor: 'var(--bd)' }}
                >
                  🔊
                </button>
              ) : null}
            </div>
          </div>
        </div>
      )}
    </Card>
  );
}
